import { useCallback, useMemo, useRef } from "react";
import { get } from "lodash";
import { useQuery } from "@tanstack/react-query";

import { queryClient } from "App";
import UserService from "services/User";
import { addResizedColumns } from "hooks/useTableConfig";
import { getDepartmentsTableColumns } from "routes/entity/components/departments_table/DepartmentsTableColumnsAndFilters";
import { DEPARTMENTS_TABLE_CONSTANTS } from "routes/entity/components/departments_table/DepartmentsTableV3";
import { departmentKeys } from "routes/entity/components/departments_table/queryKeys";

export const useDepartmentsTable = ({ entityId = null }) => {
  const departmentModalRef = useRef(null);
  const { data: tableSettings, isSuccess: isTableSettingsFetched } = useQuery({
    queryKey: ["table-settings"],
    queryFn: UserService.getUserSettings,
  });

  const queryKey = useMemo(() => departmentKeys.list({ entityId }), [entityId]);

  const invalidateData = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey });
  }, [queryKey]);

  const columns = useMemo(() => {
    if (!isTableSettingsFetched) return [];
    // this will only be called at an event handler
    // eslint-disable-next-line react-hooks/refs
    const initialColumns = getDepartmentsTableColumns({
      entityId,
      departmentModalRef,
      invalidateData,
    });

    const departmentsTableSettings = get(
      tableSettings,
      `user_settings.preferences.tables.${DEPARTMENTS_TABLE_CONSTANTS.tableName}`,
      {}
    );

    const withResizedColumns = addResizedColumns({
      columns: initialColumns,
      resizedColumns: departmentsTableSettings?.resized_columns || {},
    });

    return withResizedColumns;
  }, [
    entityId,
    tableSettings,
    isTableSettingsFetched,
    departmentModalRef,
    invalidateData,
  ]);

  return { columns, departmentModalRef, queryKey, invalidateData };
};
